import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Loading, Radio } from '@nextui-org/react';
import { AgGridReact } from "ag-grid-react";

import { error } from "../../../lib/userAlerts";
import endpoint from "../../../config/endpoint.json";
import { mstore } from "../../../store";
import { fetchWithJson } from "../../../lib/api";

const DAYS_30 = 1;
const DAYS_365 = 2;
const DAYS_ALL = 3;

const createColumnDefs = () => [
  {
    headerName: "Driver Name",
    field: "name",
    filter: "agTextColumnFilter",
    filterParams: {
      applyButton: true,
      clearButton: true,
    },
  },
  {
    headerName: "Employee ID",
    field: "employee_id",
    filter: "agTextColumnFilter",
    filterParams: {
      applyButton: true,
      clearButton: true,
    },
    minWidth: 240,
  },
  {
    headerName: "Email",
    field: "user_email",
    suppressMenu: true,
    minWidth: 300,
  },
  { 
    headerName: "Phone", 
    field: "phone",
    suppressMenu: true,
    minWidth: 180,
  },
  { 
    headerName: "Stops Served", 
    field: "numStops", 
    suppressMenu: true,
    minWidth: 120,
  },
  {
    headerName: "Receipts Uploaded",
    field: "numReceipts",
    suppressMenu: true,
    minWidth: 120,
  },
  { 
    headerName: "Artifax Uploaded", 
    field: "documentsUploaded",
    suppressMenu: true,
    minWidth: 120,
  },
  {
    headerName: "Safety Docs Uploaded",
    field: "safetyDocsUploaded",
    suppressMenu: true,
    minWidth: 120,
  },
  {
    headerName: "Receipt Amount Claimed",
    field: "driverClaimedAmount",
    suppressMenu: true,
  }
];

const DriverStats = () => {
  const clientId = useSelector(state => state.client?.id || mstore.getPath(["user", "company_id"]));
  
  const [state, setState] = useState({
    driverList: [],
    fetching: false,
    chartType: DAYS_30,
    columnDefs: createColumnDefs(),
  });

  const gridApi = useRef(null);
  const columnApi = useRef(null);

  const onGridReady = useCallback((params) => {
    gridApi.current = params.api;
    columnApi.current = params.columnApi;
    gridApi.current.sizeColumnsToFit();
  }, []);

  const fetchDriverList = useCallback(async (clientId) => {
    setState(prev => ({
      ...prev,
      fetching: true,
      driverList: []
    }));

    try {
      const res = await fetchWithJson(endpoint.statsDriver, {
        company_id: clientId,
        allTime: state.chartType === DAYS_ALL,
        thisYear: state.chartType === DAYS_365
      });

      if (res.status !== 200) {
        error("Oops! error occurred when fetching driver stats");
        return;
      }

      const data = await res.json();
      setState(prev => ({
        ...prev,
        driverList: data.drivers
      }));
    } catch (err) {
      console.error(err);
      error("Oops! error occurred when fetching driver stats");
    } finally {
      setState(prev => ({
        ...prev,
        fetching: false
      }));
    }
  }, [state.chartType]);

  const handleChartTypeChange = useCallback((newType) => {
    setState(prev => ({ ...prev, chartType: newType }));
  }, []);

  useEffect(() => {
    if (clientId) {
      fetchDriverList(clientId);
    }
  }, [clientId, fetchDriverList]);

  const renderLoading = () => (
    <div className="flex justify-center mt6">
      <Loading size="lg">Fetching driver stats..Hang on!</Loading>
    </div>
  );

  const renderNoDrivers = () => (
    <div className="flex justify-center mt6 f4 fw6">
      You do not have any company drivers.
    </div>
  );

  const renderDriverList = () => (
    <div style={{ height: "calc(100vh - 250px)", width: "100%" }}>
      <Radio.Group 
        row 
        css={{ mt: '0.75rem', padding: 10, paddingBottom: 20 }}
        size="sm"
        value={state.chartType}
      >
        <Radio
          onChange={() => handleChartTypeChange(DAYS_30)}
          value={DAYS_30}
        >
          30 Days
          <Radio.Desc>Driver stats for last 30 days</Radio.Desc>
        </Radio>
        <Radio
          onChange={() => handleChartTypeChange(DAYS_365)}
          value={DAYS_365}
        >
          365 Days
          <Radio.Desc>Driver stats for last 365 days</Radio.Desc>
        </Radio>
        <Radio
          onChange={() => handleChartTypeChange(DAYS_ALL)}
          value={DAYS_ALL}
        >
          All Time
          <Radio.Desc>Driver stats all times</Radio.Desc>
        </Radio>
      </Radio.Group>
      <div
        style={{ height: 'calc(100vh - 300px)' }}
        className="ag-theme-quartz p-3"
      >
        <AgGridReact
          columnDefs={state.columnDefs}
          rowData={state.driverList}
          pagination={true}
          paginationAutoPageSize={true}
          onGridReady={onGridReady}
          enableColResize
          enableSorting
          enableFilter
        />
      </div>
    </div>
  );

  const height = window.innerHeight;
  const gridContainerStyle = {
    height: (height - 160) + "px",
  };

  return (
    <div className="ml2 mt2">
      {state.fetching ? (
        renderLoading()
      ) : (
        <div className="mr2 mt2">
          {state.driverList.length === 0 ? (
            renderNoDrivers()
          ) : (
            <div style={gridContainerStyle} className="ag-theme-balham">
              {renderDriverList()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DriverStats;